<script lang="ts" setup>
    import BaseButton from '@/components/BaseButton.vue';
    import BaseIcon from '@/components/BaseIcon.vue';
    import { useConfigStore } from '@/store/config.store';
    import { useTemplateStore } from '@/store/template.store';
    import { Events } from '@/services/Events';
    import { useField } from '@/composables/useField';
    import AlertImage from '@/components/AlertImage.vue';
    import IconZoomIn from '@/components/icons/IconZoomIn.vue';
    import { useMaterialUpdater } from '@/composables/useMaterialUpdater';
    import { createLockedBackgroundObjectGroup } from '@/commons/fabricjs/FabricGroupHelper';
    import { LOCKED_FOREGROUND_GROUP_NAME } from '@/littleWedding/fabricGroupsName';
    import { getGridUrl } from '@/utils/getGridUrl';
    import { useZoomControl } from '@/composables/useZoomControl';
    import { dataStore } from '@/commons/DataStore';
    import { getParentBackgroundColor } from '@/littleWedding/canvasBuilder';
    import { storeToRefs } from 'pinia';
    import { computed, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';
    import type { IFabricWorldInitParams } from '@/commons/fabricjs/IFabricWorldInitParams';

    const { currentSide, displayGrid, fZoom, isAdmin, isMobile, isProductEdition, productImages, productType } =
        storeToRefs(useConfigStore());

    const { zoomHome, zoomIn, zoomOut } = useZoomControl();
    const { addFieldToCanvas, deleteFieldInsideCanvas, filteredFields, loadFields, updateFieldsAndOrder } = useField();
    const { selectedFieldId } = storeToRefs(useTemplateStore());

    const qualityWarning = ref(false);
    let updateMaterial: () => void;

    const fabricElementRef = useTemplateRef<HTMLCanvasElement | null>('fabricElement');

    const shouldDisplayZoomButtons = computed(() => !isMobile.value || !selectedFieldId.value);

    const debugLockedElement = false;
    async function initCanvas() {
        const fabricWorld = dataStore.getFabricWorld(currentSide.value);
        // eslint-disable-next-line prefer-destructuring
        updateMaterial = useMaterialUpdater(filteredFields, fabricWorld).updateMaterial;
        const fabricParams: IFabricWorldInitParams = {
            canvas: null,
            enableRetinaScaling: true,
            includeDefaultValues: false, // do not export default values toJSON (shorter json)
            absolutePan: true,
            selection: false,
            allowTouchScrolling: false, // Changed: disable to prevent conflicts
            allowTouchPan: true,
            preserveObjectStacking: true,
            productType: productType.value,
            parentBackgroundColor: getParentBackgroundColor(isProductEdition.value, isAdmin.value),
            backgroundColor: 'transparent',
            transparentBackground: true,
        };
        fabricWorld.init({ ...fabricParams, canvas: fabricElementRef.value });
        fabricWorld.displayImageFullCanvas();
        if (fabricWorld.fabricCanvas) {
            createLockedBackgroundObjectGroup(fabricWorld.fabricCanvas, LOCKED_FOREGROUND_GROUP_NAME);
        }
        const gridUrl = await getGridUrl('recto', productType.value, productImages.value);
        if (gridUrl) {
            await fabricWorld.createGridHelper(gridUrl, isProductEdition.value);
            fabricWorld.setGridHelperVisibility(true);
            displayGrid.value = true;
        }
        fZoom.value = fabricWorld.fabricCanvas?.getZoom() ?? 1;

        if (filteredFields.value.length) {
            if (debugLockedElement) {
                filteredFields.value.map((field) => {
                    field.editable = false;
                    return field;
                });
            }
            loadFields(
                filteredFields.value,
                {
                    fabricWorld,
                    select: false,
                    isAdmin: isAdmin.value,
                    isProductEdition: isProductEdition.value,
                },
                updateMaterial,
            ).then(() => {
                updateFieldsAndOrder(fabricWorld, filteredFields.value);
                fabricWorld.displayImageFullCanvas();
            });
        }

        Events.on('field-selected', selectedFieldCallback);
        Events.on('image-quality-warning', qualityCheckCallback);

        updateMaterial();
        fabricWorld.displayImageFullCanvas();
    }
    onMounted(async () => {
        initCanvas();
    });

    onUnmounted(() => {
        selectedFieldCallback(undefined);
        Events.off('field-selected', selectedFieldCallback);
        Events.off('image-quality-warning', qualityCheckCallback);
        window.removeEventListener('load', initCanvas);
    });

    watch(filteredFields, (newValues, oldValues) => {
        const fabricWorld = dataStore.getFabricWorld(currentSide.value);
        if (newValues.length === oldValues.length) {
            updateFieldsAndOrder(fabricWorld, newValues);
            return;
        }
        if (newValues.length < oldValues.length) {
            deleteFieldInsideCanvas(fabricWorld, oldValues, newValues);
            return;
        }
        const diffField = newValues.filter((newField) => !oldValues.some((oldField) => oldField.id === newField.id));
        if (diffField.length === 0) {
            return;
        }
        addFieldToCanvas(fabricWorld, diffField[0], updateMaterial);
    });

    watch(displayGrid, (newValue) => {
        const fabricWorld = dataStore.getFabricWorld(currentSide.value);
        fabricWorld.setGridHelperVisibility(newValue);
    });

    function selectedFieldCallback(fieldId: string | undefined) {
        selectedFieldId.value = fieldId;
    }

    function qualityCheckCallback(args: { hasQualityWarning: boolean }) {
        qualityWarning.value = args.hasQualityWarning;
    }
</script>

<template>
    <div :class="[isMobile && 'h-dvh', !isMobile && 'h-full', 'bg-bg border-border overflow-contain relative z-0 border']">
        <AlertImage :show-alert="qualityWarning" />
        <canvas
            ref="fabricElement"
            :class="[isMobile && 'h-dvh', !isMobile && 'h-full', 'relative w-full']"
        />
        <div
            v-if="shouldDisplayZoomButtons"
            :class="[
                'z-10 flex gap-3',
                !isMobile && 'absolute bottom-12 right-2 mr-8 flex-col',
                isMobile && 'fixed bottom-28 left-1/2 -translate-x-1/2 flex-row',
            ]"
        >
            <BaseButton
                class="pointer-events-auto h-9 w-9"
                @click="zoomHome"
            >
                <span class="sr-only">
                    {{ $t('zoom') }}
                </span>
                <IconZoomIn />
            </BaseButton>
            <BaseButton
                class="pointer-events-auto h-9 w-9"
                @click="zoomIn"
            >
                <span class="sr-only">
                    {{ $t('zoom_in') }}
                </span>
                <BaseIcon
                    size="lg"
                    name="MagnifyingGlassPlusIcon"
                />
            </BaseButton>
            <BaseButton
                class="pointer-events-auto h-9 w-9"
                @click="zoomOut"
            >
                <span class="sr-only">
                    {{ $t('zoom_out') }}
                </span>
                <BaseIcon
                    size="lg"
                    name="MagnifyingGlassMinusIcon"
                />
            </BaseButton>
        </div>
    </div>
</template>
